import { Injectable, Inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppState } from "../models/app.state";
import { ENV_VARS } from "../environment-variables/environment-variables.module";
import { HttpClient } from "@angular/common/http";
import { UserState } from "../store/reducers/user.reducer";
import * as ManualItemActions from "../store/actions/manual-item.actions";
import { FhirService } from "./fhir.service";
import { v4 } from "uuid";
import { IManualItem, ManualItem } from "../models/manual-item";
import { TranslateService } from "@ngx-translate/core";
import { DashboardService } from "./dashboard.service";
import {
  manualItemTypes,
  Score,
  DiaprofilTopic,
  topicNames
} from "../models/types";

export function uuid(): string {
  return v4().replace(/-/g, "");
}

interface ManualItemSearchResult {
  "@id": string;
  batching: {
    "@id": string;
    first: string;
    last: string;
    next: string;
  };
  items: ManualItemResult[];
  items_total: number;
}

interface ManualItemResult {
  "@id": string;
  id: string;
  sourceUrl: string;
  title: string;
  itemType: { title: string; token: manualItemTypes };
  questionnaireItemId: string;
  consultationTopicId: string;
  consultationTopicIdVersion2: string;
  patientWishId: string;
  scoreId: Score;
  dashboardAreaId: topicNames;
  manualText: string;
  relevantTopics: { title: string; token: DiaprofilTopic }[];
  defaultTopics: { title: string; token: DiaprofilTopic }[];
  created: string;
  modified: string;
}

@Injectable()
export class ManualItemsService {
  manualItems: ManualItem[];
  userState: UserState;
  baseUrl: string;
  endpoints: { [key: string]: string };
  constructor(
    public http: HttpClient,
    public store: Store<AppState>,
    public fhir: FhirService,
    public translate: TranslateService,
    public dashboard: DashboardService,
    @Inject(ENV_VARS) public envVars
  ) {
    this.baseUrl = envVars.API_URL + "fhirdatabase/";
    this.endpoints = {
      manualItem: "@manualitem"
    };
    this.store
      .select(state => state.user)
      .subscribe(user => {
        this.userState = user;
      });
    this.store
      .select(state => state.manualItems)
      .subscribe(manualItems => {
        this.manualItems = manualItems.map(
          item => new ManualItem(item, translate, dashboard)
        );
      });
  }

  deleteManualItem(manualItem: Partial<IManualItem>) {
    return this.http
      .delete<IManualItem>(manualItem.sourceUrl)
      .subscribe(result => {
        this.store.dispatch(
          new ManualItemActions.DeleteAction({
            id: manualItem.id
          })
        );
      });
  }

  patchManualItem(
    manualItem: Partial<IManualItem>,
    extraAction?: (result: IManualItem) => void
  ) {
    return this.http
      .patch<IManualItem>(manualItem.sourceUrl, manualItem)
      .subscribe(
        result => {
          this.store.dispatch(
            new ManualItemActions.ReplaceAction({
              id: manualItem.id,
              sourceUrl: manualItem.sourceUrl,
              title: manualItem.title,
              itemType: manualItem.itemType,
              questionnaireItemId: manualItem.questionnaireItemId,
              consultationTopicId: manualItem.consultationTopicId,
              consultationTopicIdVersion2:
                manualItem.consultationTopicIdVersion2,
              patientWishId: manualItem.patientWishId,
              scoreId: manualItem.scoreId,
              dashboardAreaId: manualItem.dashboardAreaId,
              manualText: manualItem.manualText,
              relevantTopics: manualItem.relevantTopics,
              defaultTopics: manualItem.defaultTopics,
              created: manualItem.created,
              modified: manualItem.modified
            })
          );
          if (extraAction !== undefined) {
            extraAction(result);
          }
        },
        // Errors will call this callback instead:
        err => {
          console.log(err);
        }
      );
  }
  postManualItem(
    manualItem: Partial<IManualItem>,
    extraAction?: (result: ManualItemResult) => void
  ) {
    return this.http
      .post<ManualItemResult>(
        `${this.baseUrl}${this.userState.organizationUuid}/${this.endpoints.manualItem}`,
        {
          "@type": "ManualItem",
          id: uuid(),
          ...manualItem
        }
      )
      .subscribe(
        result => {
          this.store.dispatch(
            new ManualItemActions.ReplaceAction({
              id: result.id,
              sourceUrl: result["@id"],
              title: result.title,
              itemType: result.itemType.token,
              questionnaireItemId: result.questionnaireItemId,
              consultationTopicId: result.consultationTopicId,
              consultationTopicIdVersion2: result.consultationTopicIdVersion2,
              patientWishId: result.patientWishId,
              scoreId: result.scoreId,
              dashboardAreaId: result.dashboardAreaId,
              manualText: result.manualText,
              relevantTopics: Array.isArray(result.relevantTopics)
                ? result.relevantTopics.map(topic => topic.token)
                : null,
              defaultTopics: Array.isArray(result.defaultTopics)
                ? result.defaultTopics.map(topic => topic.token)
                : null,
              created: result.created,
              modified: result.modified
            })
          );
          if (extraAction !== undefined) {
            extraAction(result);
          }
        },
        // Errors will call this callback instead:
        err => {
          console.log(err);
        }
      );
  }

  getManualItems(
    extraAction?: (result: ManualItemSearchResult) => void,
    url?: string
  ) {
    if (url === undefined) {
      url = `${this.baseUrl}${this.userState.organizationUuid}/${this.endpoints.manualItem}?portal_type=ManualItem`;
    }
    return this.http.get<ManualItemSearchResult>(url).subscribe(
      result => {
        if (
          result.hasOwnProperty("batching") &&
          result.batching.next !== null &&
          result.batching["@id"] !== result.batching.last
        ) {
          this.getManualItems(extraAction, result.batching.next);
        }
        const mappedItems = result.items.map(item => {
          return {
            id: item.id,
            sourceUrl: item["@id"],
            title: item.title,
            itemType: item.itemType.token,
            questionnaireItemId: item.questionnaireItemId,
            consultationTopicId: item.consultationTopicId,
            consultationTopicIdVersion2: item.consultationTopicIdVersion2,
            patientWishId: item.patientWishId,
            scoreId: item.scoreId,
            dashboardAreaId: item.dashboardAreaId,
            manualText: item.manualText,
            relevantTopics: Array.isArray(item.relevantTopics)
              ? item.relevantTopics.map(topic => topic.token)
              : null,
            defaultTopics: Array.isArray(item.defaultTopics)
              ? item.defaultTopics.map(topic => topic.token)
              : null,
            created: item.created,
            modified: item.modified
          };
        });
        this.store.dispatch(
          new ManualItemActions.ReplaceMultipleAction(mappedItems)
        );
        if (extraAction !== undefined) {
          extraAction(result);
        }
      },
      // Errors will call this callback instead:
      err => {
        console.log(err);
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/manual-items.service.ts